import { useRef, useState } from 'react';
import { examTypes, subjects } from '@/mocks/examData';

interface PdfUploadPanelProps {
  examName: string;
  subject: string;
  examType: string;
  onExamChange: (patch: { examName?: string; subject?: string; examType?: string }) => void;
  onFileSelected: (file: File) => void;
  onClearPdf: () => void;
  fileName: string | null;
  loading: boolean;
  error: string | null;
  capturedCount: number;
  noteCount: number;
  onGoToNotes: () => void;
}

export default function PdfUploadPanel(props: PdfUploadPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f: File | undefined | null) => {
    if (!f) return;
    props.onFileSelected(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const fieldClass =
    'w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100';
  const labelClass = 'text-xs font-semibold text-foreground-600';

  return (
    <div className="flex flex-col gap-5">
      {/* 파일 업로드 */}
      <div>
        <p className={`${labelClass} mb-2`}>시험지 업로드 (PDF)</p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-7 text-center transition ${
            dragOver
              ? 'border-primary-400 bg-primary-50'
              : 'border-background-300 bg-background-100/60 hover:border-primary-300 hover:bg-primary-50/50'
          }`}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-upload-cloud-2-line text-xl"></i>
          </span>
          <p className="text-sm font-medium text-foreground-800">클릭하거나 PDF를 끌어다 놓으세요</p>
          <p className="text-xs text-foreground-500">PDF가 열리면 문제 영역을 드래그해서 캡처할 수 있어요</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>

        {props.loading && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-3 text-sm text-foreground-700">
            <i className="ri-loader-4-line animate-spin text-base text-primary-600"></i>
            PDF를 여는 중이에요...
          </div>
        )}

        {props.error && (
          <div className="mt-3 rounded-lg border border-accent-200 bg-accent-50 p-3 text-sm text-accent-800">
            <i className="ri-error-warning-line mr-1"></i>
            {props.error}
          </div>
        )}

        {props.fileName && !props.loading && (
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-file-pdf-2-line text-2xl"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground-800">{props.fileName}</p>
              <p className="text-xs text-foreground-500">캡처 {props.capturedCount}문제</p>
            </div>
            <button
              type="button"
              onClick={props.onClearPdf}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-foreground-700"
              aria-label="PDF 닫기"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        )}
      </div>

      {/* 사용 방법 */}
      <div className="rounded-lg border border-secondary-200 bg-secondary-50/70 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-secondary-900">
          <i className="ri-magic-line text-sm"></i>
          이렇게 사용하세요
        </p>
        <ol className="flex flex-col gap-1.5 text-[11px] leading-relaxed text-foreground-600">
          <li className="flex gap-1.5">
            <span className="font-bold text-secondary-700">1.</span>
            시험지 PDF를 업로드하면 오른쪽에 시험지가 열려요.
          </li>
          <li className="flex gap-1.5">
            <span className="font-bold text-secondary-700">2.</span>
            오답노트에 넣을 문제 위에서 마우스로 사각형을 그려요.
          </li>
          <li className="flex gap-1.5">
            <span className="font-bold text-secondary-700">3.</span>
            손을 떼면 그 영역이 바로 오답노트 문제칸에 들어가요.
          </li>
        </ol>
      </div>

      {/* 시험 정보 */}
      <div>
        <p className={`${labelClass} mb-2`}>시험 정보 (캡처되는 노트에 함께 저장돼요)</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-foreground-500">시험 유형</label>
            <select className={fieldClass} value={props.examType} onChange={(e) => props.onExamChange({ examType: e.target.value })}>
              {examTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-foreground-500">과목</label>
            <select className={fieldClass} value={props.subject} onChange={(e) => props.onExamChange({ subject: e.target.value })}>
              {subjects.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs text-foreground-500">시험명</label>
          <input
            className={fieldClass}
            value={props.examName}
            onChange={(e) => props.onExamChange({ examName: e.target.value })}
            placeholder="예: 2025학년도 수능 수학"
          />
        </div>
      </div>

      {/* 오답노트로 이동 */}
      <button
        type="button"
        onClick={props.onGoToNotes}
        className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
      >
        <i className="ri-file-text-line text-base"></i>
        오답노트 확인하기
        <span className="rounded-full bg-background-50/25 px-2 py-0.5 text-xs font-bold">{props.noteCount}</span>
      </button>
    </div>
  );
}